/**
 * Fonte de dados do modo `prd`: sessão e conteúdo vêm do backend.
 *
 * O que ainda não tem rota no servidor (plano `tablet-frontend`, notas da `INT1`):
 * - nome do participante e do grupo no login: o rótulo usa o que foi digitado e o id do grupo;
 * - `GET /conteudos` e `GET /pontuacao/ranking` (routers vazios) e o aviso de ranking liberado;
 * (As anotações do grupo e o rascunho ao vivo do Modo Versus já não faltam: as anotações
 * vão ao servidor, IN10, e o rascunho é publicado por ele ao grupo, IN13.)
 */
import { TurmaAusenteError } from './AuthClient.js';
import { FASES_DO_GRUPO, limparProgressoDaFase, mudancaDaEstacao, MUDANCA } from '../game/services/estacaoDoGrupo.js';
import { faseEmAndamento } from '../game/services/fluxo.js';
import { paraAtividades } from '../game/services/labPixels.js';
import { completarCores, sortearCor } from '../game/services/mural.js';
import { paraJornada } from '../game/services/player1.js';
import { paraEnigmas } from '../game/services/scanPixel.js';
import { paraAnexos, paraVersus } from '../game/services/versus.js';
import { criarMenuResolucaoApi } from './menuResolucaoApi.js';

const CODIGO_GRUPO_NAO_INICIADO = 'grupo_nao_iniciado';

// Fase dona do bônus; vira parâmetro de caminho na rota do servidor (`/estacoes/{fase}/bonus/selecao`).
const FASE_DO_BONUS = 'labpixels';
const CHAVE_CORES = 'fiat-arcade:cores-insights:v1';
const CHAVE_CONCLUIDAS = (participanteId) => `fiat-arcade:fases-concluidas:v1:${participanteId}`;
const CHAVE_ESTACOES = (participanteId) => `fiat-arcade:estacoes:v1:${participanteId}`;
const CHAVE_ANOTACOES = (participanteId) => `fiat-arcade:versus-anotacoes:v1:${participanteId}`;

/** Anotação do Versus como a tela a usa; `minha` libera o "apagar" só para a autora. */
const paraAnotacao = (nota) => ({ id: nota.id, texto: nota.texto, autor: nota.autor_nome ?? '', minha: nota.minha === true });

const paraNota = (insight) => ({
    id: insight.id,
    texto: insight.texto,
    criadoEm: insight.criado_em,
    atualizadoEm: insight.atualizado_em
});

/** Material do servidor → o que a tela Materiais usa. O PDF não tem endereço público: é baixado com a sessão. */

function paraMaterial (item) {
    return {
        id: item.id, titulo: item.titulo, desbloqueado: Boolean(item.desbloqueado),
        url: null, temArquivo: Boolean(item.tem_arquivo)
    };
}

/**
 * Os grupos terminam o Scan Pixel quase juntos, e cada um dispara o POST final (com o histórico
 * inteiro) no mesmo instante. Esperar de 0 a este valor antes de enviar espalha o pico no servidor.
 */
export const JITTER_ENVIO_SCANPIXEL_MS = 3000;

const esperarReal = (ms) => new Promise((resolver) => setTimeout(resolver, ms));

export function criarFonteApi ({
    auth, api, rede, dispositivo, codigo = null,
    armazenamento = globalThis.localStorage, aleatorio = Math.random, esperar = esperarReal
}) {
    // O backend guarda só o texto da nota (`InsightSaida`): a cor sorteada
    // (RN-INS-02) fica no tablet, indexada pelo id do insight.
    const lerCores = () => {
        try { return JSON.parse(armazenamento?.getItem(CHAVE_CORES) ?? '{}'); } catch { return {}; }
    };
    let cores = lerCores();
    const gravarCores = () => {
        try { armazenamento?.setItem(CHAVE_CORES, JSON.stringify(cores)); } catch { /* sem storage */ }
    };

    const estado = {
        total: 5,
        concluidas: 0,
        insightsDisponivel: true,
        materiaisDisponivel: true,
        rankingHabilitado: false
    };
    let concluidas = new Set();
    const ouvintes = new Set();
    // Quem é o dono do token: as rotas do Versus levam turma e grupo no caminho.
    let sessaoAtual = null;
    // Última fase aberta que ainda não chegou a nenhuma tela. O servidor repete o
    // último comando da sala a cada inscrição, e isso pode vir antes de o Aguarde existir.
    let fasePendente = null;
    // Fases que o servidor entregou a este tablet e que ele ainda não concluiu (alimenta o "Voltar para a atividade").
    const liberadas = new Set();
    // `ordem` de cada rodada do Versus, pelo id que as cenas usam.
    let rodadasVersus = [];
    // Menu Resolução: estado, conteúdo por Pixel, resultado e envio das respostas (módulo próprio).
    const menuResolucao = criarMenuResolucaoApi({ api, rede, obterSessao: () => sessaoAtual, armazenamento });

    const lerJson = (chave, padrao) => {
        try { return JSON.parse(armazenamento?.getItem(chave) ?? 'null') ?? padrao; } catch { return padrao; }
    };
    const gravarJson = (chave, valor) => {
        try { armazenamento?.setItem(chave, JSON.stringify(valor)); } catch { /* sem storage */ }
    };
    // O Menu Resolução é da turma e o servidor diz se o módulo está aberto. A marca local de "concluído" nasce
    // quando o módulo é finalizado (e ele fica encerrado); se o facilitador o reabriu (REINICIAR MÓDULO), a marca
    // ficou velha e quem já tinha jogado precisa poder entrar na rodada nova. Módulo ainda encerrado: nada muda,
    // inclusive a repetição do evento pelo servidor.
    const entregarMenuResolucao = async () => {
        if (!(await menuResolucao.faseEmAndamento())) return;
        if (concluidas.delete('menuresolucao') && sessaoAtual?.participanteId) {
            gravarJson(CHAVE_CONCLUIDAS(sessaoAtual.participanteId), [...concluidas]);
        }
        entregarFase('menuresolucao');
    };
    const entregarFase = (fase) => {
        // Fase já concluída neste tablet não reabre com a repetição do servidor.
        if (concluidas.has(fase)) return;
        // A janela da turma abre com o primeiro grupo; quem entra é só o grupo que o
        // facilitador iniciou. Os demais seguem no Aguarde (e voltam a ele ao sair de Materiais).
        if (FASES_DO_GRUPO[fase] && estacoes[fase]?.estado !== 'iniciado') return;
        liberadas.add(fase);
        if (ouvintes.size === 0) {
            fasePendente = fase;
            return;
        }
        fasePendente = null;
        for (const callback of ouvintes) callback(fase);
    };
    // Estado do meu grupo em cada estação, como o servidor informou por último
    // (`estacao_grupo_atualizada` ou `GET /estacoes/estado`). Fica salvo: o ciclo
    // é o que diz, depois de um recarregamento, se o facilitador reiniciou o grupo.
    let estacoes = {};
    const progressoPendente = {};       // { [fase]: maior progresso ainda não aceito pelo servidor }
    // Modo Versus: o grupo só vê a pontuação depois que o facilitador libera os resultados.
    let resultadosVersusLiberados = false;
    const ouvintesEstacao = new Set();
    const aplicarEstacao = (novo) => {
        if (!novo?.fase) return;
        // O evento é da sala do grupo; a conferência evita aplicar estado de outro grupo.
        if (sessaoAtual?.grupoId && novo.grupo_id && novo.grupo_id !== sessaoAtual.grupoId) return;
        if (novo.fase === 'versus' && novo.ciclo >= (estacoes.versus?.ciclo ?? 0)) {
            const liberou = Boolean(novo.resultados_liberados) && !resultadosVersusLiberados;
            resultadosVersusLiberados = Boolean(novo.resultados_liberados);
            if (liberou) buscarPlacarVersus();
        }
        const conhecida = estacoes[novo.fase] !== undefined;
        const mudanca = mudancaDaEstacao(estacoes[novo.fase] ?? null, novo);
        if (!mudanca) return;
        // Estação que este tablet nunca viu começar: o progresso guardado da fase é de outra rodada
        // (um teste anterior, outro ciclo). Sem o descarte a fase abriria direto na pontuação. Quem
        // recarrega no meio do jogo tem o ciclo salvo, e o progresso fica.
        if (mudanca === MUDANCA.INICIADA && !conhecida && sessaoAtual?.participanteId) {
            limparProgressoDaFase(novo.fase, sessaoAtual.participanteId, armazenamento);
            if (concluidas.delete(novo.fase)) gravarJson(CHAVE_CONCLUIDAS(sessaoAtual.participanteId), [...concluidas]);
        }
        estacoes = { ...estacoes, [novo.fase]: { estado: novo.estado, ciclo: novo.ciclo, liberado: novo.liberado === true } };
        if (mudanca === MUDANCA.FINALIZADA) liberadas.delete(novo.fase);
        // Só o ciclo é salvo: depois de recarregar, o estado é sempre relido do servidor.
        if (sessaoAtual?.participanteId) {
            gravarJson(CHAVE_ESTACOES(sessaoAtual.participanteId),
                Object.fromEntries(Object.entries(estacoes).map(([fase, e]) => [fase, { ciclo: e.ciclo }])));
        }

        // Grupo em andamento: quem está no Aguarde entra na fase (quem já está nela não é afetado).
        if (mudanca === MUDANCA.INICIADA || mudanca === MUDANCA.RETOMADA) entregarFase(novo.fase);
        // `automatico`: o grupo finalizou sozinho, porque todos concluíram (RN-EF-023).
        // `automatico` só vale para o evento em que o grupo finalizou sozinho; a liberação do facilitador tira todos.
        const automatico = novo.automatico === true && !(mudanca === MUDANCA.FINALIZADA && novo.liberado === true);
        for (const callback of ouvintesEstacao) {
            callback({
                fase: novo.fase, mudanca, ...(novo.liberado === true ? { liberado: true } : {}), ...(automatico ? { automatico: true } : {})
            });
        }
    };

    const ouvintesVisibilidade = new Set();
    const aplicarVisibilidade = (visibilidade) => {
        const novo = {
            insightsDisponivel: visibilidade?.insights ?? estado.insightsDisponivel,
            materiaisDisponivel: visibilidade?.materiais ?? estado.materiaisDisponivel,
            rankingHabilitado: visibilidade?.ranking ?? estado.rankingHabilitado
        };
        const mudou = Object.keys(novo).some((chave) => novo[chave] !== estado[chave]);
        Object.assign(estado, novo);
        if (mudou) for (const callback of ouvintesVisibilidade) callback({ ...novo });
    };

    const baseVersus = () => {
        if (!sessaoAtual?.turmaId || !sessaoAtual?.grupoId) throw new Error('Sessão sem turma ou grupo.');
        return `/versus/turmas/${encodeURIComponent(sessaoAtual.turmaId)}/grupos/${encodeURIComponent(sessaoAtual.grupoId)}`;
    };
    const ordemDaRodada = (rodadaId) => {
        const rodada = rodadasVersus.find((r) => r.id === rodadaId);
        if (!rodada) throw new Error(`Rodada desconhecida: ${rodadaId}`);
        return rodada.ordem;
    };
    /**
     * `placar` do servidor → o que a tela de pontuação espera. Só vale com as duas
     * rodadas lançadas e depois de o facilitador liberar os resultados ao grupo.
     */
    const avisarPlacar = (placar) => {
        if (!placar?.completa || !resultadosVersusLiberados) return;
        // Mesma conversão do evento `versus_total_apurado`: sem ela a nota por critério não casa e aparece "-".
        for (const callback of ouvintesAvaliacao) callback({ rodadas: paraRodadasDoPlacar(placar.rodadas), total: placar.total ?? null });
    };
    const buscarPlacarVersus = () => {
        if (!sessaoAtual) return;
        api.requisitar('GET', `/versus/turmas/${encodeURIComponent(sessaoAtual.turmaId)}/grupos/${encodeURIComponent(sessaoAtual.grupoId)}/estado`)
            .then((estado) => avisarPlacar(estado?.placar))
            .catch((falha) => console.error('[Versus] placar indisponível.', falha));
    };
    const ouvintesResultado = new Set();
    const ouvintesBonus = new Set();
    const ouvintesArgumento = new Set();
    const ouvintesAnotacoes = new Set();
    const ouvintesAvaliacao = new Set();
    const ouvintesRodadaEncerrada = new Set();
    const ouvintesSegundoCase = new Set();
    const avisarBonus = (evento) => { for (const callback of ouvintesBonus) callback(evento); };
    // O estado do grupo traz a bônus desde a liberação: o sorteio e, se o sorteado já conferiu, a opção.
    // Serve a quem perdeu os eventos (tablet reconectando, ainda no modal do sorteio): chegam na ordem
    // em que aconteceram, como se tivessem acabado de passar.
    const avisarBonusDoEstado = (linhas) => {
        const lab = (Array.isArray(linhas) ? linhas : []).find((linha) => linha?.fase === FASE_DO_BONUS
            && (!linha.grupo_id || !sessaoAtual?.grupoId || linha.grupo_id === sessaoAtual.grupoId));
        const bonus = lab?.bonus;
        if (!bonus?.sorteado?.id) return;
        avisarBonus({
            tipo: 'liberado',
            sorteado: { id: bonus.sorteado.id, nome: bonus.sorteado.nome ?? '' },
            semente: bonus.semente ?? null
        });
        if (bonus.conferido === true && bonus.opcao_id != null) avisarBonus({ tipo: 'conferido', opcaoId: bonus.opcao_id });
    };
    // Placar do servidor (`{ordem, total, notas: [{criterio_versus_id, valor}]}`) -> `{ordem, total, notas: [{criterio_id, nota}]}`.
    const paraRodadasDoPlacar = (rodadas = []) => rodadas.map((r) => ({
        ordem: r.ordem,
        total: r.total,
        notas: (r.notas ?? []).map((n) => ({ criterio_id: n.criterio_versus_id, nota: n.valor }))
    }));
    const avisarAvaliacao = (avaliacao) => { for (const callback of ouvintesAvaliacao) callback(avaliacao); };

    // Eventos do servidor chegam planos, com o nome em `type`
    // (docs/scan-pixel-integracao.md §6.2); só alcançam quem está inscrito
    // na sala da turma — a inscrição é feita em src/main.js.
    const cancelarMensagens = rede.on('mensagem', (mensagem) => {
        switch (mensagem?.type) {
            // O par `_grupo` existe porque a classificação do difusor é por `type`:
            // o anúncio de um grupo é `ROOM_ONLY` e o da turma é `GLOBAL`, e um
            // tipo só derrubaria a barreira entre salas. Aqui o tratamento é o
            // mesmo (`DP-INT-089`): a sala já garante o escopo — o servidor resolve
            // `grupo:{id}` com a turma da sessão —, e conferir `grupo_id` de novo
            // duplicaria a regra em dois lugares que podem divergir.
            case 'admin_fase_aberta':
            case 'admin_fase_aberta_grupo':
                if (mensagem.fase === 'menuresolucao') void entregarMenuResolucao();
                else if (mensagem.fase) entregarFase(mensagem.fase);
                break;
            // Insights, Materiais e Ranking: o facilitador liga e desliga para a turma.
            case 'visibilidade_atualizada':
                aplicarVisibilidade(mensagem);
                break;
            case 'scanpixel_resultado_sincronizado':
                for (const callback of ouvintesResultado) {
                    callback({
                        grupoId: mensagem.grupo_id, pontuacao: mensagem.pontuacao, colocacao: mensagem.colocacao ?? null
                    });
                }
                break;
            // Bônus do Laboratório: o facilitador libera e o servidor sorteia quem responde.
            case 'labpixels_bonus_liberada':
                avisarBonus({
                    tipo: 'liberado',
                    sorteado: { id: mensagem.participante_sorteado_id, nome: mensagem.participante_sorteado_nome ?? '' },
                    semente: mensagem.semente ?? null
                });
                break;
            case 'labpixels_bonus_selecao':
                avisarBonus({ tipo: mensagem.conferido ? 'conferido' : 'selecao', opcaoId: mensagem.opcao_id });
                break;
            // Modo Versus: catálogo, chat e conversa já existem no backend real. O rascunho
            // do líder (`versus_argumento_rascunho`) já é publicado pelo backend (IN13):
            // ROOM_ONLY na sala do grupo, evento plano, sem eco ao remetente (`AC-INT-055`).
            case 'versus_argumento_rascunho':
                for (const callback of ouvintesArgumento) {
                    callback({ tipo: 'rascunho', rodadaId: mensagem.rodada_id, texto: mensagem.texto ?? '' });
                }
                break;
            // Alguém do grupo criou uma anotação: quem está com o pop-up aberto relê a lista (sem texto no evento).
            case 'versus_anotacoes_atualizadas':
                for (const callback of ouvintesAnotacoes) callback();
                break;
            // `AC-INT-020`: o encerramento da rodada (argumento do líder ou facilitador) é o gatilho do avanço.
            case 'versus_rodada_encerrada':
                for (const callback of ouvintesRodadaEncerrada) {
                    // `IN18`/`DP-INT-098`: "tem texto" = string (sem trim; `""` e só espaços valem); o resto é `null`.
                    const texto = typeof mensagem.texto === 'string' ? mensagem.texto : null;
                    callback({ ordem: mensagem.ordem, origem: mensagem.origem, encerradaEm: mensagem.encerrada_em, texto });
                }
                break;
            // `V12`/`DP-MV-065`: o facilitador liberou o case 2 para o grupo (sala do grupo).
            case 'versus_segundo_case_liberado':
                for (const callback of ouvintesSegundoCase) callback();
                break;
            // O facilitador conduz grupo a grupo: iniciar, pausar, retomar, reiniciar, finalizar.
            case 'estacao_grupo_atualizada':
                aplicarEstacao(mensagem);
                break;
            // Evento real do backend (`RN-MV-051`): só total por rodada e total geral. Cédula de uma
            // rodada só (`completa: false`) não libera a pontuação do grupo.
            case 'versus_total_apurado':
                if (mensagem.completa === false) break;
                // O servidor publica a cada cédula; o tablet retém até o facilitador liberar (DP-EF-015).
                if (!resultadosVersusLiberados) break;
                avisarAvaliacao({ rodadas: paraRodadasDoPlacar(mensagem.rodadas), total: mensagem.total ?? null });
                break;
            case 'admin_fase_encerrada':
            case 'admin_fase_encerrada_grupo':
                if (mensagem.fase === 'labpixels') avisarBonus({ tipo: 'indisponivel' });
                if (fasePendente === mensagem.fase) fasePendente = null;
                liberadas.delete(mensagem.fase);
                break;
            default:
        }
    });

    return {
        modo: 'prd',

        /** O login pede o código de acesso na tela quando o build não traz um (`VITE_CODIGO`). */
        exigeCodigo: !codigo,

        /** Nenhum APK nasce com turma fixa e o app não a lembra: o login sempre pede a turma. */
        exigeTurma: true,

        /**
         * `POST /auth/login/participante`: turma + matrícula (o "ID" da tela) +
         * código de acesso. A tela não pede nome: o exibido vem do servidor.
         */
        async autenticar ({ id, codigo: codigoDigitado = null, turma: turmaDigitada = null }) {
            const turmaDoLogin = String(turmaDigitada ?? '').trim();
            if (!turmaDoLogin) throw new TurmaAusenteError();
            const sessao = await auth.loginParticipante({
                turmaId: turmaDoLogin,
                matricula: String(id).trim(),
                codigo: String(codigoDigitado ?? codigo ?? '').trim(),
                dispositivo
            });

            return {
                token: sessao.token, papel: sessao.papel, teste: false,
                participanteId: sessao.participante_id,
                nome: sessao.nome ?? String(id).trim(),            // RN-AP-002 / RN-AP-025: nunca o nome digitado
                grupo: sessao.grupo_nome ?? `Grupo ${sessao.grupo_id}`,
                grupoId: sessao.grupo_id, turmaId: sessao.turma_id
            };
        },

        /** Guarda de quem é a sessão: escopo das rotas do Versus e do progresso salvo neste tablet. */
        definirAutor (participanteId, sessao = null) {
            sessaoAtual = {
                participanteId, turmaId: sessao?.turmaId ?? null, grupoId: sessao?.grupoId ?? null,
                nome: sessao?.nome ?? ''
            };
            concluidas = new Set(lerJson(CHAVE_CONCLUIDAS(participanteId), []));
            estacoes = Object.fromEntries(Object.entries(lerJson(CHAVE_ESTACOES(participanteId), {}))
                .map(([fase, e]) => [fase, { estado: null, ciclo: e?.ciclo ?? 0 }]));
            menuResolucao.iniciarSessao();
        },

        // ------------------------------------------------- estação do grupo

        /**
         * Login: o servidor é a fonte do estado das estações e das fases concluídas, e substitui o que o
         * tablet tinha guardado (de um login anterior, de outro grupo, de um ciclo que o facilitador
         * reiniciou). O ciclo de cada estação passa a ser o do servidor; o progresso de uma fase só
         * sobrevive se o ciclo que o tablet guardou for o mesmo. O `estado` fica `null`, como em
         * `definirAutor`: a leitura que o WebSocket dispara ao conectar entrega as fases em curso.
         * Sem resposta do servidor, nada muda e o login segue (a sincronização do `pronto` tenta de novo).
         */
        async recarregarEstadoDoServidor () {
            if (!sessaoAtual?.participanteId) return;
            let linhas;
            try {
                linhas = await api.requisitar('GET', '/estacoes/estado');
            } catch (falha) {
                console.error('[Estacao] estado do servidor indisponível no login.', falha?.codigo ?? falha?.message);
                return;
            }
            const { participanteId } = sessaoAtual;
            const doGrupo = (Array.isArray(linhas) ? linhas : [])
                .filter((linha) => linha?.fase && (!linha.grupo_id || linha.grupo_id === sessaoAtual.grupoId));
            const guardadas = lerJson(CHAVE_ESTACOES(participanteId), {});
            for (const linha of doGrupo) {
                if (guardadas[linha.fase]?.ciclo !== linha.ciclo) limparProgressoDaFase(linha.fase, participanteId, armazenamento);
            }
            estacoes = Object.fromEntries(doGrupo.map((linha) => [linha.fase, { estado: null, ciclo: linha.ciclo ?? 0 }]));
            concluidas = new Set(doGrupo.filter((linha) => linha.estado === 'finalizado').map((linha) => linha.fase));
            liberadas.clear();
            fasePendente = null;
            gravarJson(CHAVE_ESTACOES(participanteId), Object.fromEntries(Object.entries(estacoes).map(([fase, e]) => [fase, { ciclo: e.ciclo }])));
            gravarJson(CHAVE_CONCLUIDAS(participanteId), [...concluidas]);
            // O WebSocket também sincroniza ao conectar e pode ter terminado antes desta troca: relê para
            // que as fases em curso sejam entregues sobre o estado novo (o `estado: null` acima vira a mudança).
            await this.sincronizarEstacoes();
        },

        aoEstacao (callback) {
            ouvintesEstacao.add(callback);
            return () => ouvintesEstacao.delete(callback);
        },

        /**
         * `GET /estacoes/estado`: o estado do meu grupo em cada estação. Chamado ao
         * conectar — o evento pode ter passado com o tablet fora do ar.
         */
        async sincronizarEstacoes () {
            if (!sessaoAtual) return;
            try {
                const linhas = await api.requisitar('GET', '/estacoes/estado');
                linhas.forEach(aplicarEstacao);
                // Ao reconectar: o `labpixels_bonus_selecao` que passou com o tablet fora do ar volta daqui.
                avisarBonusDoEstado(linhas);
            } catch (falha) {
                console.error('[Estacao] estado do grupo indisponível.', falha);
            }
            // O Menu Resolução é da turma inteira (sem estado por grupo): quem entrou depois da abertura
            // não recebeu o evento, então a fase aberta é lida do estado do módulo.
            if (await menuResolucao.faseEmAndamento()) entregarFase('menuresolucao');
            try {
                aplicarVisibilidade(await api.requisitar('GET', '/estacoes/visibilidade'));
            } catch (falha) {
                console.error('[Estacao] visibilidade indisponível.', falha);
            }
        },

        /** Avisa quando o facilitador liga ou desliga Insights, Materiais ou Ranking. */
        aoVisibilidade (callback) {
            ouvintesVisibilidade.add(callback);
            return () => ouvintesVisibilidade.delete(callback);
        },

        /**
         * `PUT /estacoes/{fase}/progresso`: alimenta o painel do facilitador. É
         * aviso, não resultado: se falhar, o jogo segue e a pontuação não muda.
         */
        registrarProgresso (fase, progresso) {
            if (!sessaoAtual || !Number.isInteger(progresso) || progresso < 0) return Promise.resolve();
            // O envio que falha fica anotado: no Scan Pixel é ele que libera o material no servidor.
            progressoPendente[fase] = Math.max(progressoPendente[fase] ?? 0, progresso);
            return Promise.resolve()
                .then(() => api.requisitar('PUT', `/estacoes/${fase}/progresso`, { progresso }))
                .then(() => { if (progressoPendente[fase] === progresso) delete progressoPendente[fase]; })
                .catch((falha) => {
                    // Recusa do servidor (o grupo não está na estação): refazer o mesmo envio nunca dará
                    // certo, e a pendência era reenviada a cada abertura de Materiais. Só falha de rede
                    // (sem código) continua pendente.
                    if (falha?.codigo === CODIGO_GRUPO_NAO_INICIADO && progressoPendente[fase] === progresso) {
                        delete progressoPendente[fase];
                    }
                    console.warn(`[Estacao] progresso de ${fase} não enviado.`, falha?.codigo ?? falha?.message);
                });
        },

        /** O facilitador reiniciou o grupo: a fase deixa de constar como concluída e pode ser aberta de novo. */
        reabrirFase (fase) {
            concluidas.delete(fase);
            if (sessaoAtual?.participanteId) gravarJson(CHAVE_CONCLUIDAS(sessaoAtual.participanteId), [...concluidas]);
            entregarFase(fase);
        },

        /** `GET /labpixels/catalogo`: baixado uma vez, com o gabarito; a fase segue offline. */
        async obterCatalogoLabPixels () {
            return paraAtividades(await api.requisitar('GET', '/labpixels/catalogo'));
        },

        async enviarResultadoLabPixels ({ corpo, chave }) {
            const resposta = await api.enviarGzip('/labpixels/resultado', corpo, { 'Idempotency-Key': chave });
            return { pontuacao: resposta?.pontuacao ?? null };
        },

        async enviarResultadoGrupoLabPixels ({ corpo, chave }) {
            const resposta = await api.enviarGzip('/labpixels/resultado-grupo', corpo, { 'Idempotency-Key': chave });
            return { pontuacao: resposta?.pontuacao ?? null };
        },

        aoBonusLab (callback) {
            ouvintesBonus.add(callback);
            return () => ouvintesBonus.delete(callback);
        },

        /**
         * Em `prd` quem libera a bônus é o facilitador; o tablet espera o evento. Mas o evento é único:
         * se o facilitador liberou antes de este tablet chegar à espera, ele já passou. Por isso, ao
         * entrar na espera (e na pergunta da bônus), relê o estado do grupo e, se a bônus já estiver
         * liberada (ou conferida), avisa como se os eventos tivessem chegado agora.
         */
        solicitarBonusLab () {
            api.requisitar('GET', '/estacoes/estado')
                .then(avisarBonusDoEstado)
                .catch((falha) => console.error('[Lab] estado da bônus indisponível.', falha?.codigo ?? falha?.message));
        },

        /**
         * RI-LAB-B02: o tablet do sorteado espelha a escolha para o grupo
         * (`POST /estacoes/labpixels/bonus/selecao`); conferir conclui a bônus no servidor.
         */
        publicarBonusLab ({ tipo, opcaoId }) {
            Promise.resolve()
                .then(() => api.requisitar('POST', `/estacoes/${FASE_DO_BONUS}/bonus/selecao`, {
                    opcao_id: String(opcaoId), conferido: tipo === 'conferido'
                }))
                .catch((falha) => console.error('[Lab] escolha da bônus não enviada.', falha?.codigo ?? falha?.message));
        },

        /** `GET /player1/catalogo`: uma requisição, todo o conteúdo; a estação segue offline. */
        async obterCatalogoPlayer1 () {
            return paraJornada(await api.requisitar('GET', '/player1/catalogo'));
        },

        /**
         * `POST /player1/resultado` (gzip + Idempotency-Key). A jornada é
         * imutável no servidor: `409 fase_ja_sincronizada` quer dizer que um
         * envio anterior já valeu, e para o tablet isso é sucesso
         * (docs/player1-integracao.md §7.5). Os demais erros sobem.
         */
        async enviarResultadoPlayer1 ({ corpo, chave }) {
            try {
                await api.enviarGzip('/player1/resultado', corpo, { 'Idempotency-Key': chave });
            } catch (falha) {
                if (falha?.codigo !== 'fase_ja_sincronizada') throw falha;
            }
            return { aceito: true };
        },

        // ------------------------------------------------- Menu Resolução
        // O painel que a cena lê, o envio das respostas e a pontuação final: ver `menuResolucaoApi.js`.

        obterMenuResolucao: () => menuResolucao.obterMenuResolucao(),
        registrarRespostaPixel: (dados) => menuResolucao.registrarRespostaPixel(dados),
        registrarRespostasPixel3: (dados) => menuResolucao.registrarRespostasPixel3(dados),
        respostaMenuResolucaoEnviada: (dados) => menuResolucao.respostaEnviada(dados),
        obterMinhaPontuacao: () => menuResolucao.obterMinhaPontuacao(),

        // ------------------------------------------------------- Modo Versus
        // O catálogo, o chat (`POST .../mensagens`) e a leitura da conversa existem no
        // backend real, assim como o estado do grupo, o argumento, o encerramento da rodada,
        // o placar (`versus_total_apurado`) e as anotações do grupo.

        /**
         * Catálogo + estado do grupo. `eh_lider` vem do estado (o catálogo é o
         * mesmo para todos); se o estado falhar, ninguém escreve o argumento.
         */
        async obterCatalogoVersus () {
            const catalogo = await api.requisitar('GET', '/versus/catalogo');
            let estado = null;
            try {
                estado = await api.requisitar('GET', `/versus/turmas/${encodeURIComponent(sessaoAtual.turmaId)}/grupos/${encodeURIComponent(sessaoAtual.grupoId)}/estado`);
            } catch (falha) {
                console.error('[Versus] estado do grupo indisponível.', falha);
            }
            const versus = paraVersus({ ...catalogo, lider: estado?.eh_lider === true });
            // Os anexos do chat são os PDFs da biblioteca que o grupo já liberou. Se a
            // biblioteca falhar, o chat abre do mesmo jeito, só sem o que anexar.
            versus.materiais = paraAnexos(await this.obterMateriais().catch((falha) => {
                console.error('[Versus] biblioteca indisponível; chat sem anexos.', falha);
                return [];
            }));
            rodadasVersus = versus.rodadas.map(({ id, ordem }) => ({ id, ordem }));
            return versus;
        },

        /** Síncrono e sem fila: a resposta da IA vem na própria requisição; se a rede cair, o erro sobe. */
        async enviarMensagemVersus ({ turmaId, grupoId, ordem, texto, materiais = [] }) {
            const resposta = await api.requisitar('POST', `/versus/turmas/${turmaId}/grupos/${grupoId}/rodadas/${ordem}/mensagens`, {
                texto, tags_selecionadas: materiais
            });
            const textoDaIa = resposta?.mensagem_ia?.texto;
            if (typeof textoDaIa !== 'string' || textoDaIa.trim() === '') {
                // `DP-INT-035`: 200 sem texto da IA não é queda de rede; `classificarFalhaChat` o reconhece pelo `codigo`.
                throw Object.assign(new Error('Resposta do chat sem texto da IA (mensagem_ia).'), { codigo: 'resposta_invalida' });
            }
            return { texto: textoDaIa };
        },

        /** Histórico do servidor (`AC-INT-017`): `participante` -> `eu`, `tags_selecionadas` -> `materiais`. */
        async obterConversaVersus ({ turmaId, grupoId, ordem, participanteId }) {
            const conversa = await api.requisitar(
                'GET', `/versus/turmas/${turmaId}/grupos/${grupoId}/rodadas/${ordem}/participantes/${participanteId}/mensagens`
            );
            return (conversa?.mensagens ?? []).map((m) => ({
                autor: m.autor === 'participante' ? 'eu' : 'ia',
                texto: m.texto,
                materiais: m.tags_selecionadas ?? []
            }));
        },

        // Anotações do grupo (`DP-INT-010`): sem edição, e cada um apaga só a sua (`minha`, RN-MV-10b).
        // O servidor tira turma, grupo e autor da sessão; `autor_nome` é o nome cadastrado do autor.
        async listarAnotacoesVersus () {
            const notas = await api.requisitar('GET', '/versus/anotacoes');
            const { ocultas } = lerJson(CHAVE_ANOTACOES(sessaoAtual?.participanteId), { ocultas: [] });
            return notas
                .filter((nota) => !ocultas.includes(nota.id))
                .map(paraAnotacao);
        },

        /**
         * Troca de case: as anotações do case anterior saem da vista (o servidor guarda todas, sem rodada,
         * e continua sendo a fonte). Roda uma vez por case: voltar ao mesmo case não apaga nada.
         */
        async limparAnotacoesVersus (ordem) {
            if (!sessaoAtual?.participanteId) return;
            const chave = CHAVE_ANOTACOES(sessaoAtual.participanteId);
            const estado = lerJson(chave, { ordem: null, ocultas: [] });
            if (estado.ordem === ordem) return;
            if (estado.ordem !== null || ordem > 1) {
                const notas = await api.requisitar('GET', '/versus/anotacoes');
                estado.ocultas = [...new Set([...estado.ocultas, ...notas.map((nota) => nota.id)])];
            }
            gravarJson(chave, { ordem, ocultas: estado.ocultas });
        },

        /** Avisa quando o grupo muda as anotações (evento de sala `versus_anotacoes_atualizadas`). */
        aoAnotacoesVersus (callback) {
            ouvintesAnotacoes.add(callback);
            return () => ouvintesAnotacoes.delete(callback);
        },

        async criarAnotacaoVersus ({ texto }) {
            return paraAnotacao(await api.requisitar('POST', '/versus/anotacoes', { texto }));
        },

        /**
         * Apaga a própria anotação (o servidor recusa a alheia com 404, e avisa a sala do grupo, que relê
         * a lista). `404` aqui é "já foi apagada" (outro aparelho, ou o evento chegou antes): o resultado
         * que o jogador queria já vale, então não vira erro.
         */
        async excluirAnotacaoVersus (nota) {
            try {
                await api.requisitar('DELETE', `/versus/anotacoes/${encodeURIComponent(nota.id)}`);
            } catch (falha) {
                if (falha?.status !== 404) throw falha;
            }
        },

        aoArgumentoVersus (callback) {
            ouvintesArgumento.add(callback);
            return () => ouvintesArgumento.delete(callback);
        },

        /**
         * `AC-INT-019`: o servidor decide a liderança (`eh_lider` não autoriza, `RN-INT-005`); o 403 e os
         * 409 sobem como `ApiErro`. A `ordem` vai no path; o corpo é só `{ texto }`.
         */
        async enviarArgumentoVersus ({ turmaId, grupoId, ordem, texto }) {
            const gravado = await api.requisitar('POST', `/versus/turmas/${turmaId}/grupos/${grupoId}/rodadas/${ordem}/argumento`, { texto });
            return { texto: gravado.texto };
        },

        /** `AC-INT-018`: estado do grupo (líder, rodadas, placar) lido do servidor; datas ficam como vêm. */
        async obterEstadoVersus ({ turmaId, grupoId }) {
            const estadoDoGrupo = await api.requisitar('GET', `/versus/turmas/${turmaId}/grupos/${grupoId}/estado`);
            return {
                ehLider: estadoDoGrupo.eh_lider,
                rodadas: estadoDoGrupo.rodadas.map((r) => ({
                    ordem: r.ordem, status: r.status, encerradaEm: r.encerrada_em, rascunho: r.rascunho ?? null,
                    // `V12`: servidor sem o campo é o de antes, sem trava do case 2.
                    liberada: r.liberada !== false
                })),
                placar: {
                    rodadas: paraRodadasDoPlacar(estadoDoGrupo.placar.rodadas),
                    total: estadoDoGrupo.placar.total,
                    completa: estadoDoGrupo.placar.completa
                }
            };
        },

        /** RN-MV-13: o tablet do líder espelha o rascunho para o grupo. */
        publicarRascunhoVersus ({ rodadaId, texto }) {
            rede.sendAction?.('versus_argumento_rascunho', { rodada_id: rodadaId, texto });
        },

        aoRodadaEncerradaVersus (callback) {
            ouvintesRodadaEncerrada.add(callback);
            return () => ouvintesRodadaEncerrada.delete(callback);
        },

        /** `V12`: o facilitador liberou o case 2 do grupo. */
        aoSegundoCaseLiberadoVersus (callback) {
            ouvintesSegundoCase.add(callback);
            return () => ouvintesSegundoCase.delete(callback);
        },

        /** Em `prd` o rascunho (IN13) e o encerramento da rodada chegam por evento; nada a simular. */
        acompanharArgumentoVersus () {},

        aoAvaliacaoVersus (callback) {
            ouvintesAvaliacao.add(callback);
            return () => ouvintesAvaliacao.delete(callback);
        },

        /**
         * Em `prd` quem libera a pontuação é o facilitador e o aviso chega por evento. Quem
         * entrou na espera depois dele (queda de rede, recarga) relê a liberação em
         * `GET /estacoes/estado` e, só se já estiver liberada, lê o placar do estado do grupo
         * (DP-EF-015, AC-EF-018).
         */
        async solicitarAvaliacaoVersus ({ turmaId, grupoId } = {}) {
            if (!turmaId || !grupoId) return;
            try {
                await this.sincronizarEstacoes();
                if (!resultadosVersusLiberados) return;
                const { placar } = await this.obterEstadoVersus({ turmaId, grupoId });
                if (placar.completa) avisarAvaliacao({ rodadas: placar.rodadas, total: placar.total });
            } catch (falha) {
                console.warn('[Versus] placar indisponível; aguardando o evento.', falha);
            }
        },

        /**
         * `GET /conteudos`: a biblioteca, com o que o grupo já liberou. Quem decide é o
         * servidor, a partir dos enigmas resolvidos — por isso um progresso do Scan Pixel
         * que não chegou é reenviado antes de ler a lista.
         */
        async obterMateriais () {
            if (progressoPendente.scanpixel) await this.registrarProgresso('scanpixel', progressoPendente.scanpixel);
            return (await api.requisitar('GET', '/conteudos')).map(paraMaterial);
        },

        /** `GET /conteudos/{id}/arquivo`: o PDF de um material liberado, com a sessão do participante. */
        baixarMaterial (material) {
            return api.baixar(`/conteudos/${encodeURIComponent(material.id)}/arquivo`);
        },

        /** `GET /pontuacao/ranking`: já ordenado pelo servidor, conforme o destino de cada fase. */
        async obterRanking () {
            const ranking = await api.requisitar('GET', '/pontuacao/ranking');
            const linha = ({ nome, pontos }) => ({ nome, pontos });
            return { grupos: (ranking.grupos ?? []).map(linha), individual: (ranking.individual ?? []).map(linha) };
        },

        /** `GET /pontuacao/regras`: quanto vale cada coisa na fase, para a tela mostrar. */
        async obterRegrasDePontuacao (fase) {
            return (await api.requisitar('GET', '/pontuacao/regras'))?.fases?.[fase] ?? null;
        },

        /** `GET /scanpixel/catalogo`: baixado uma vez; a fase segue offline. */
        async obterCatalogoScanPixel () {
            const enigmas = paraEnigmas(await api.requisitar('GET', '/scanpixel/catalogo'));
            // Cada enigma mostra o material que libera (RN-SP-06). A biblioteca é rota à parte:
            // se falhar, a fase abre do mesmo jeito, só sem o nome do material no aviso.
            const materiais = await api.requisitar('GET', '/conteudos').catch(() => []);
            return enigmas.map((enigma) => {
                const material = materiais.find((item) => item.enigma_ordem === enigma.ordem);
                return material ? { ...enigma, material: { id: material.id, titulo: material.titulo } } : enigma;
            });
        },

        /** O progresso informado é o que libera o material do enigma no servidor (RN-MAT-01). */
        concluirEnigma (enigma) {
            return this.registrarProgresso('scanpixel', enigma?.ordem);
        },

        /**
         * `POST /scanpixel/resultado` (gzip + Idempotency-Key). O `202` não
         * traz a colocação: ela chega depois, por `aoResultadoScanPixel`.
         *
         * `concluidos` vai junto como progresso da estação: é ele que diz ao servidor que este
         * participante terminou, e com todos terminados o grupo finaliza sozinho (RN-EF-023).
         */
        async enviarResultadoScanPixel ({ corpo, chave, concluidos }) {
            await esperar(Math.floor(aleatorio() * JITTER_ENVIO_SCANPIXEL_MS));
            const resposta = await api.enviarGzip('/scanpixel/resultado', corpo, { 'Idempotency-Key': chave });
            if (concluidos !== undefined) this.registrarProgresso('scanpixel', concluidos);
            return { colocacao: undefined, pontuacao: resposta?.pontuacao ?? null };
        },

        /** Resultado que o grupo já fechou (o servidor o mantém depois de um reinício): `{colocacao}` ou `null`. */
        async lerResultadoScanPixel () {
            const resultado = await api.requisitar('GET', '/scanpixel/resultado');
            return resultado?.sincronizado ? { colocacao: resultado.colocacao ?? undefined } : null;
        },

        aoResultadoScanPixel (callback) {
            ouvintesResultado.add(callback);
            return () => ouvintesResultado.delete(callback);
        },

        async listarInsights () {
            const insights = await api.requisitar('GET', '/insights');
            const notas = completarCores(insights.map(paraNota), cores, aleatorio);
            cores = Object.fromEntries(notas.map((nota) => [nota.id, nota.cor]));
            gravarCores();
            return notas;
        },

        async criarInsight ({ texto, cor = null }) {
            const nota = paraNota(await api.requisitar('POST', '/insights', { texto }));
            nota.cor = cor ?? sortearCor(Object.values(cores), aleatorio);
            cores[nota.id] = nota.cor;
            gravarCores();
            return nota;
        },

        async atualizarInsight (id, { texto }) {
            const nota = paraNota(await api.requisitar('PUT', `/insights/${id}`, { texto }));
            return { ...nota, cor: cores[id] };
        },

        async excluirInsight (id) {
            await api.requisitar('DELETE', `/insights/${id}`);
            delete cores[id];
            gravarCores();
        },

        /** Fase aberta e não concluída neste tablet, ou `null`: o Aguarde oferece voltar a ela. */
        faseEmAndamento () {
            return faseEmAndamento({
                liberadas: [...liberadas],
                estados: Object.fromEntries(Object.entries(estacoes).map(([fase, e]) => [fase, e.estado])),
                concluidas: [...concluidas]
            });
        },

        async obterEstadoAguarde () {
            return { ...estado, concluidas: concluidas.size };
        },

        registrarConclusao (fase) {
            concluidas.add(fase);
            liberadas.delete(fase);
            if (fasePendente === fase) fasePendente = null;
            if (sessaoAtual?.participanteId) gravarJson(CHAVE_CONCLUIDAS(sessaoAtual.participanteId), [...concluidas]);
        },

        aoLiberarFase (callback) {
            ouvintes.add(callback);
            // Fase aberta enquanto nenhuma tela escutava (ex.: app reaberto com sessão salva).
            if (fasePendente) {
                const fase = fasePendente;
                fasePendente = null;
                if (!concluidas.has(fase)) callback(fase);
            }
            return () => ouvintes.delete(callback);
        },

        encerrar () {
            cancelarMensagens();
            menuResolucao.encerrar();
            ouvintes.clear();
        }
    };
}
